import {
  useCreateCompany,
  useCurrentCompany,
  useSetRepairersActivity,
  useUpdateCurrentCompany,
} from 'app/core/api/company/api-company';
import {
  CREATE_COMPANY_REQ_KEYS,
  PageDetails,
} from 'app/core/api/company/api-company.types';
import FetchifyAddressForm, {
  DetailedAddress,
} from 'app/shared/components/fetchify-address/FetchifyAddressForm';
import { ErrorText } from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import { Opt } from 'app/tools/general.types';
import {
  useEffectOnce,
  useEffectOnChanges,
  useObservableSend,
} from 'app/tools/hooks/misc-hooks';
import { useObservableEagerState } from 'observable-hooks';
import React, { useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  clearBusinessProfileTempKeys,
  patchBusinessProfileTemp,
  useGetBusinessProfileTempOnce,
  useMovePage,
} from '../create-business-profile/business-profile-utils';
import BottomPageAndSubmitActions from '../create-business-profile/input-pages/components/BottomActions';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

type FormValues = DetailedAddress;

type Props = {
  firstTime?: boolean;
  isIndividual?: boolean;
};
export default function CreateCompanyAddress({
  firstTime = false,
  isIndividual = false,
}: Props): JSX.Element {
  useSetPageTitle(isIndividual ? 'Address' : 'Company Address');

  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Company Address',
    };
    repairActivity(pageDetails).subscribe();
  });

  const history = useHistory();
  const handleCritErr = useErrorHandler();
  const { nextPage } = useMovePage();
  const { showSnack } = useShowInfo();

  const createCoApi = useCreateCompany();
  const updateCoApi = useUpdateCurrentCompany();
  const { resource: company, loading } = useCurrentCompany({
    fresh: true,
  });
  const autoSaved = useGetBusinessProfileTempOnce();
  const defaultValues = loading ? undefined : company || autoSaved;

  const { error, sending } = firstTime ? createCoApi : updateCoApi;
  const { request$: createCo$ } = createCoApi;
  const { request$: updateCo$ } = updateCoApi;

  // Allows autosaving current entered values on prev page btn click
  /** setGetValues has to be in the form `() => func` otherwise the
   *  set state will call the function and evaluate it rather than
   *  saving the function entity (set state will always call a
   *  function if it is given as the parameter)
   */
  const [getValues, setGetValues] = useState<
    () => Opt<Partial<FormValues>>
  >(() => undefined);

  // Trigger a submission programmatically (eg. submit btn outside
  // form component)
  const [sendCommand, command$] = useObservableSend<'submit'>();
  const editable$ = useMemo(
    () => new BehaviorSubject(firstTime),
    [firstTime]
  );
  const editable = useObservableEagerState(editable$);

  const onSubmit = (details: DetailedAddress) => {
    let request$: Observable<unknown>;
    const companyAddress = {
      ...details,
      // if country is missing because code is not found, backend
      // validation will suffice to let the user know
      country: details.country.code || '',
    };

    if (firstTime) {
      const { name, ...others } = autoSaved;
      if (!name) {
        handleCritErr('Company name missing from previous screen');
        return;
      }

      request$ = createCo$({
        ...others,
        ...{ name },
        ...companyAddress,
        companyacctype: 'business',
      });
    } else {
      request$ = updateCo$(companyAddress);
    }

    const successActionText = firstTime ? 'created' : 'updated';
    return request$.pipe(
      tap(() => {
        clearBusinessProfileTempKeys(CREATE_COMPANY_REQ_KEYS);
        showSnack({
          message: `Company successfully ${successActionText}!`,
        });
        nextPage();
      })
    );
  };

  // Click cancel button -> get navigated back automatically
  useEffectOnChanges(() => {
    if (!editable && !sending && !error) {
      history.goBack();
    }
  }, [editable, error, history, sending]);

  return (
    <>
      {!isIndividual && (
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Enter your business address
        </Body>
      )}
      <Body tiny>Asterisks (*) indicate required fields</Body>
      <ErrorText message={error} />
      <FetchifyAddressForm
        {...{
          defaultValues,
          error,
          onSubmit,
          command$,
          setGetValues,
          editable,
        }}
      />
      {firstTime ? (
        <BottomPageAndSubmitActions
          isSubmit
          onBack={() => {
            const values = getValues();
            // Autosave (cache) unsubmitted values
            if (values) {
              patchBusinessProfileTemp({
                ...values,
                country: values.country?.code || '',
              });
            }
          }}
          onSubmit={() => sendCommand('submit')}
          {...{ sending }}
        />
      ) : (
        <BottomEditActions
          onSave={() => sendCommand('submit')}
          {...{ editable$, loading, sending }}
        />
      )}
    </>
  );
}
